import React from 'react';
import { ArrowLeft, Cookie, Settings, ShieldCheck, BarChart3, Info } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const isMarkdownSeparatorRow = (line: string): boolean => {
    if (!line.startsWith('|') || !line.endsWith('|')) return false;

    const separator = line.slice(1, -1);
    return separator.length > 0 && [...separator].every(character =>
        character === '-' || character === ':' || character === '|' || character.trim() === ''
    );
};

const CookiePolicy: React.FC = () => {
    const navigate = useNavigate();
    const { languageManager } = useLanguage();
    const legal = languageManager.legal;
    const cookiePolicy = legal.cookie_policy;

    return (
        <div className="min-h-screen bg-[#FBFBFB] flex flex-col font-sans selection:bg-orange-100">
            {/* Navigation */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl py-3`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-14">
                        <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
                            <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/10 group-hover:scale-105 transition-transform duration-300">
                                <ShieldCheck className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-lg font-bold text-slate-900 tracking-tight">Lashing Calculator</span>
                        </div>
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-all font-semibold text-sm px-4 py-2 rounded-xl hover:bg-slate-100"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {legal.return_home}
                        </button>
                    </div>
                </div>
            </nav>

            <main className="flex-grow pt-20">
                {/* Hero Section */}
                <div className="bg-slate-900 relative overflow-hidden py-16 sm:py-24">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0 bg-[url('/shipping_background.png')] bg-cover bg-center grayscale"></div>
                        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-transparent to-slate-900"></div>
                    </div>
                    <div className="max-w-4xl mx-auto px-6 relative z-10 text-center sm:text-left">
                        <div className="flex items-center justify-center sm:justify-start gap-2 mb-4 text-slate-400 font-mono text-xs uppercase tracking-[0.2em]">
                            <span>Information</span>
                            <span>/</span>
                            <span>Legal</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
                            {cookiePolicy.title}
                        </h1>
                        <div className="flex items-center justify-center sm:justify-start gap-4">
                            <span className="bg-white/10 px-3 py-1 rounded-full border border-white/10 text-slate-400 text-sm font-medium">
                                {cookiePolicy.last_updated}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="max-w-4xl mx-auto px-6 py-20 lg:py-32">
                    <div className="space-y-20 lg:space-y-28">
                        {legal.detailed_cookie_policy ? (
                            legal.detailed_cookie_policy.map((section, index) => (
                                <section key={index} className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight flex items-center gap-4">
                                        <div className="p-2 bg-slate-100 rounded-xl group-hover:bg-slate-200 transition-colors">
                                            {index === 0 ? <Info className="w-6 h-6 text-slate-600" /> :
                                                index === 3 ? <Settings className="w-6 h-6 text-slate-600" /> :
                                                    index === 8 ? <ShieldCheck className="w-6 h-6 text-slate-600" /> :
                                                        <Cookie className="w-6 h-6 text-slate-600" />}
                                        </div>
                                        {section.title}
                                    </h2>
                                    <div className="space-y-6">
                                        {(() => {
                                            // Group consecutive pipe-table lines into a single table block
                                            type Block = { type: 'table'; rows: string[][] } | { type: 'text'; value: string };
                                            const blocks: Block[] = [];
                                            let tableLines: string[] = [];
                                            const flushTable = () => {
                                                if (tableLines.length > 0) {
                                                    const parsed = tableLines
                                                        .filter(l => !isMarkdownSeparatorRow(l)) // remove separator rows
                                                        .map(l => l.split('|').filter((_, i, a) => i > 0 && i < a.length - 1).map(c => c.trim()));
                                                    blocks.push({ type: 'table', rows: parsed });
                                                    tableLines = [];
                                                }
                                            };
                                            section.content.forEach(line => {
                                                if (line.startsWith('|')) {
                                                    tableLines.push(line);
                                                } else {
                                                    flushTable();
                                                    if (line.trim() !== '') blocks.push({ type: 'text', value: line });
                                                }
                                            });
                                            flushTable();
                                            return blocks.map((block, bi) => {
                                                if (block.type === 'table') {
                                                    const [header, ...body] = block.rows;
                                                    return (
                                                        <div key={bi} className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                                                            <table className="w-full text-sm text-left text-slate-600">
                                                                <thead className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                                                                    <tr>
                                                                        {header.map((h, hi) => (
                                                                            <th key={hi} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-slate-100">
                                                                    {body.map((row, ri) => (
                                                                        <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                                                                            {row.map((cell, ci) => (
                                                                                <td key={ci} className="px-4 py-3 leading-relaxed">{cell}</td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <p key={bi} className="text-[18px] leading-[26px] text-[#575656] font-normal">
                                                        {block.value}
                                                    </p>
                                                );
                                            });
                                        })()}
                                        {index === 0 && (
                                            <div className="mt-8 p-6 bg-blue-50/50 border border-blue-100 rounded-2xl flex items-start gap-4">
                                                <ShieldCheck className="w-6 h-6 text-blue-600 mt-1 shrink-0" />
                                                <p className="text-[16px] text-blue-800 leading-relaxed font-medium">
                                                    This policy is part of our commitment to your privacy. Please also read our <Link to="/privacy" className="font-bold underline underline-offset-4 decoration-blue-300 hover:decoration-blue-600 transition-all">Privacy Policy</Link> for comprehensive details on how we protect your data.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            ))
                        ) : (
                            <div className="space-y-20">
                                {/* Fallback content */}
                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight flex items-center gap-4">
                                        <Info className="w-6 h-6 text-slate-600" />
                                        {cookiePolicy.intro_title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {cookiePolicy.intro_content}
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight flex items-center gap-4">
                                        <Settings className="w-6 h-6 text-slate-600" />
                                        {cookiePolicy.types_title}
                                    </h2>
                                    <div className="grid sm:grid-cols-2 gap-8">
                                        <div className="p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                                            <div className="flex items-center gap-3 mb-4">
                                                <ShieldCheck className="w-5 h-5 text-green-600" />
                                                <h3 className="font-bold text-slate-900 text-lg uppercase tracking-tight">{cookiePolicy.essential_title}</h3>
                                            </div>
                                            <p className="text-[16px] text-slate-600 leading-relaxed">{cookiePolicy.essential_desc}</p>
                                        </div>

                                        <div className="p-8 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                                            <div className="flex items-center gap-3 mb-4">
                                                <BarChart3 className="w-5 h-5 text-blue-600" />
                                                <h3 className="font-bold text-slate-900 text-lg uppercase tracking-tight">{cookiePolicy.analytics_title}</h3>
                                            </div>
                                            <p className="text-[16px] text-slate-600 leading-relaxed">{cookiePolicy.analytics_desc}</p>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight flex items-center gap-4">
                                        <Settings className="w-6 h-6 text-slate-600" />
                                        {cookiePolicy.manage_title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {cookiePolicy.manage_content}
                                    </p>
                                </section>

                                <section className="pt-20 border-t border-slate-200 text-center sm:text-left">
                                    <h2 className="text-[24px] font-bold text-slate-900 mb-6 uppercase tracking-widest">{cookiePolicy.contact_title}</h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656] mb-8 max-w-2xl">
                                        {cookiePolicy.contact_text}
                                    </p>
                                    <div className="inline-flex items-center gap-4 bg-orange-600 text-white px-8 py-4 rounded-2xl font-mono text-lg shadow-xl shadow-orange-600/20">
                                        <span className="opacity-75 font-bold tracking-tight">info@lashingplan.com</span>
                                    </div>
                                </section>
                            </div>
                        )}
                    </div>

                    <div className="mt-28 lg:mt-40 pt-16 border-t border-slate-200 text-center text-slate-400 text-xs uppercase tracking-[0.3em] font-bold">
                        Secure & Compliant Lashing Operations
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-200 py-16 px-6 mt-auto">
                <div className="max-w-4xl mx-auto flex flex-col items-center gap-8 text-center">
                    <div className="flex items-center gap-4 group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/10 group-hover:scale-110 transition-transform">
                            <ShieldCheck className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xl font-bold text-slate-900 tracking-tight">Lashing Calculator</span>
                    </div>
                    <div className="space-y-4 uppercase tracking-[0.3em] text-[10px] font-bold text-slate-400">
                        <p>&copy; {new Date().getFullYear()} Lashing Calculator</p>
                        <p>A service provided by JADE CONSULTANCY AND TRADING LTD</p>
                        <p>All rights reserved</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default CookiePolicy;
